import React, { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { FaStar, FaMapMarkerAlt, FaAngleLeft, FaAngleRight } from 'react-icons/fa'
import { s3Static } from 'utils/constants'
import { normalizeLocality } from 'utils/trainer-locality'

// Single trainer card. Avatar is smaller on mobile (64px) than on desktop (96px)
// to keep the card height down and avoid over-fetching image bytes on phones.
//
// Variants differ only in sizing - content and markup are shared so the
// surfaces cannot drift apart.
//
//   hero     - narrow fixed-width card for the above-the-fold strip
//   carousel - peeking card for the hub's horizontal mobile scroller
//   stack    - full-width card for pages where the list is the main content
//
// "stack" is the default: a page that does not opt in should render a plain
// vertical list rather than hiding most of its trainers off-screen.
export const TrainerCard = ({ sp, variant = 'stack' }) => {
    const maxRating = [1, 2, 3, 4, 5]
    const isHero = variant === 'hero'
    // Stored userLocation is free text and often a bare postal code; normalise for
    // display so cards never render "550227" as a place. See utils/trainer-locality.
    const locality = normalizeLocality(sp.userLocation)

    const avatar = (
        <span className={'relative shrink-0 rounded-xl overflow-hidden '
            + (isHero ? ' w-14 h-14 ' : ' w-16 h-16 md:w-24 md:h-24 ')}>
            <Image
                src={sp.profilePic}
                fill
                style={{objectFit: 'cover'}}
                placeholder='blur'
                blurDataURL={s3Static + "placeholder.png"}
                alt={`${sp.userFirstName} ${sp.userLastName ?? ''} - personal trainer in ${locality}`}
                sizes={isHero ? "56px" : "(max-width: 768px) 64px, 96px"}
            />
        </span>
    )

    // Name + rating + locality. Sits beside the avatar in both layouts.
    const identity = (
        <>
            <h3 className='text-base font-bold text-white group-hover:text-orange-300 transition-colors'>
                <span>{sp.userFirstName} {sp.userLastName}</span>
            </h3>
            <div className='flex flex-wrap items-center gap-x-2 gap-y-1 mt-1'>
                {sp.rating > 0 ?
                    <div className='flex items-center gap-1'>
                        <div className={'text-white text-[11px] px-1.5 py-0.5 rounded-full ' +
                            (sp.rating >= 4 ? " bg-green-600 " :
                            (sp.rating >= 3 ? " bg-blue-600 " :
                            (sp.rating >= 2 ? " bg-cyan-600 " :
                            (sp.rating >= 1 ? " bg-amber-500 " : " bg-red-600 "))))
                        }>
                            {sp.rating?.toFixed(1)}
                        </div>
                        <div className="flex gap-0.5 text-xs">
                            {[...maxRating].map((star, x) =>
                                <FaStar
                                    key={x}
                                    className={x < sp.rating ? 'text-amber-300' : 'text-white/20'}
                                />
                            )}
                        </div>
                    </div>
                    :
                    // Roughly a third of the roster has no reviews yet. Without a
                    // fallback the card renders a blank row where the rating sits.
                    <span className='text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-white/15 border border-white/20 text-white/80'>
                        Certified
                    </span>
                }
                <div className='flex items-center gap-1 text-[11px] text-white/60'>
                    <FaMapMarkerAlt/>
                    <span>{locality}</span>
                </div>
            </div>
        </>
    )

    const bio = sp.description &&
        <p className={'text-white/70 line-clamp-2 '
            + (isHero ? ' text-xs leading-[1.45] ' : ' text-sm leading-5 ')}>
            {sp.description}
        </p>

    const profileLink = (
        <span className={'inline-block font-medium text-orange-400 group-hover:text-orange-300 '
            + (isHero ? ' text-[11px] ' : ' text-xs ')}>
            View profile &rarr;
        </span>
    )

    // text-left is explicit: the hero renders these inside a text-center glass
    // card, which would otherwise centre every line.
    const shell = 'group text-left bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 hover:bg-white/10 hover:border-orange-400/30 transition-colors shrink-0 snap-start flex flex-col gap-2 '

    const width = isHero ? ' w-64 '
        // carousel peeks the next card on mobile; stack fills the column.
        : (variant === 'carousel' ? ' w-[78vw] sm:w-auto ' : ' w-full ')

    // Every variant stacks: [avatar | name/rating/locality] on one row, then the
    // bio and link across the FULL card width. A bio indented past the avatar
    // loses 64-96px of measure and breaks into a narrow column, which is most
    // visible in the three-column grid at lg.
    return (
        <Link
            href={`/profile/${sp.handleName}`}
            prefetch={false}
            className={shell + width}
        >
            <div className={'flex items-start ' + (isHero ? ' gap-2.5 ' : ' gap-3 md:gap-4 ')}>
                {avatar}
                <div className='min-w-0 flex-1'>
                    {identity}
                </div>
            </div>
            {bio}
            {profileLink}
        </Link>
    );
};

/**
 * Trainer list.
 *
 * `layout` "carousel" is the horizontal mobile scroller, used ONLY on the hub
 * page, where the trainer list is one section among many and a full vertical
 * list would cost several folds. Everywhere else - locality, freelance, female
 * trainer pages - the trainer list IS the primary content, so it stacks
 * vertically on mobile and nothing is hidden off-screen. That is the default.
 */
export const TopTrainersSection = ({ spList, layout = 'stack' }) => {
    const isCarousel = layout === 'carousel'
    const scrollRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    // Keep the prev/next buttons in sync with the carousel position.
    // Only meaningful on mobile - at sm+ the container is a grid with no overflow,
    // so scrollWidth === clientWidth and both buttons stay hidden.
    const updateScrollState = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el
        setCanScrollLeft(scrollLeft > 10)
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        updateScrollState()
        el.addEventListener('scroll', updateScrollState, { passive: true })
        window.addEventListener('resize', updateScrollState)
        return () => {
            el.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState, spList])

    const scrollBy = (direction) => {
        const el = scrollRef.current
        if (!el) return
        el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.85), behavior: 'smooth' })
    }

    return (
        <div className='relative'>
            <div
                ref={scrollRef}
                className={isCarousel
                    ? `flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide py-5
                       sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible`
                    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 py-5'}
            >
                {[...spList].map((sp, i) =>
                    <TrainerCard key={sp.handleName || i} sp={sp} variant={isCarousel ? 'carousel' : 'stack'} />
                )}
            </div>

            {/* Carousel controls - mobile only */}
            {isCarousel && canScrollLeft &&
                <button
                    type='button'
                    onClick={() => scrollBy(-1)}
                    aria-label='Show previous trainers'
                    className='sm:hidden absolute left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-slate-900/80 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center shadow-lg'
                >
                    <FaAngleLeft />
                </button>
            }
            {isCarousel && canScrollRight &&
                <button
                    type='button'
                    onClick={() => scrollBy(1)}
                    aria-label='Show more trainers'
                    className='sm:hidden absolute right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-slate-900/80 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center shadow-lg'
                >
                    <FaAngleRight />
                </button>
            }
        </div>
    );
};
